import { Component, Element, h, Host, Listen, Prop } from "@stencil/core";
import abTestingService from "@phn/services/ab-testing/ab-testing-constructor-service";
import { navStateStore } from "@phn/state/nav-state-store";
import { RoutingKeys } from "@phn/entities/routing-key";
import DrawerContent from "./drawer-content";
import { getAllFocusableElements } from "../double-drawer/helper";
import {
  getCurrentRoute,
  isAnimatingForward,
} from "../../services/routing-manager";
import { handleAnimationRouting } from "./animations/animations-controller";
import registerSingleDrawerAnimationSequences from "./animations/animations-register";
import {
  focusOnElement,
  getHTMLElement,
  isString,
} from "@phn/utility/helper";

@Component({
  tag: "phn-nd-single-drawer",
  scoped: true,
  styleUrl: "single-drawer.scss",
})
export class SingleDrawer {
  @Prop() breakpoint!: number;

  @Prop() locale!: string;

  @Prop() usingKeyboard = false;

  @Prop() receivedContent = false;

  @Element() el!: HTMLPhnNdSingleDrawerElement;

  @Listen("keydown", { passive: false })
  handleKeyDown(e: KeyboardEvent): void {
    if (e.key !== "Tab") {
      return;
    }

    const targetElement = getHTMLElement(e.target);
    if (targetElement === null) {
      return;
    }

    let focusableDrawerElements = getAllFocusableElements(
      this.el.querySelector(".active"),
    );
    if (focusableDrawerElements.length === 0) {
      return;
    }

    if (getCurrentRoute().startsWith(RoutingKeys.DEALER_SEARCH)) {
      focusableDrawerElements = focusableDrawerElements.filter(
        (element) => element.nodeName !== "BUTTON" && element.nodeName !== "A",
      );
    }

    const firstFocusableDrawerElement = focusableDrawerElements[0];
    const lastFocusableDrawerElement =
      focusableDrawerElements[focusableDrawerElements.length - 1];

    if (targetElement === firstFocusableDrawerElement && e.shiftKey) {
      e.preventDefault();

      if (lastFocusableDrawerElement !== null) {
        focusOnElement(lastFocusableDrawerElement);
      }

      return;
    }

    if (targetElement === lastFocusableDrawerElement && !e.shiftKey) {
      e.preventDefault();

      if (firstFocusableDrawerElement !== null) {
        focusOnElement(firstFocusableDrawerElement);
      }

      return;
    }
  }

  async componentWillLoad(): Promise<void> {
    registerSingleDrawerAnimationSequences();
    await abTestingService().init(navStateStore.state.app);
  }

  render(): HTMLPhnNdSingleDrawerElement {
    const { currentRoute, nextRoute } = handleAnimationRouting();

    return (
      <Host>
        {isString(currentRoute) ? (
          <div class="drawer active" key={currentRoute}>
            <span class="first-focus" tabIndex={-1} />
            <DrawerContent
              routingKey={currentRoute}
              breakpoint={this.breakpoint}
              locale={this.locale}
              receivedContent={this.receivedContent}
            />
          </div>
        ) : null}

        {isString(nextRoute) ? (
          <div
            class={`drawer animating`}
            style={isAnimatingForward() ? { opacity: "0" } : {}}
            key={nextRoute}
          >
            <DrawerContent
              routingKey={nextRoute}
              breakpoint={this.breakpoint}
              locale={this.locale}
              receivedContent={this.receivedContent}
            />
          </div>
        ) : null}
      </Host>
    );
  }
}
